/**
 * HarvestRequestsModal - Selection modal for importing AI-extracted items
 * 
 * Shows a list of items extracted from collaboration canvas with checkboxes
 * for selecting which items to import as product requests.
 */
import React, { useState, useMemo } from 'react';
import { requestsApi } from '../../services/api';
import { cn } from '../../lib/utils';
import { Button } from '../ui/button';
import { Checkbox } from '../ui/checkbox';
import { Badge } from '../ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '../ui/dialog';
import {
  Sparkles,
  Layers,
  Users,
  Target,
  Rocket,
  Loader2,
  CheckSquare,
  Square,
  FileInput,
  Quote,
  Bug,
} from 'lucide-react';
import { toast } from 'sonner';

// Type configuration - Order: Bug, Feature, Epic, Initiative, Goal, Persona
const TYPE_CONFIG = {
  bug: {
    color: 'bg-red-500',
    textColor: 'text-red-600 dark:text-red-400',
    bgColor: 'bg-red-50 dark:bg-red-950/30',
    icon: Bug,
    label: 'Bug'
  },
  feature: { 
    color: 'bg-blue-500', 
    textColor: 'text-blue-600 dark:text-blue-400',
    bgColor: 'bg-blue-50 dark:bg-blue-950/30',
    icon: Sparkles, 
    label: 'Feature' 
  },
  epic: { 
    color: 'bg-purple-500', 
    textColor: 'text-purple-600 dark:text-purple-400',
    bgColor: 'bg-purple-50 dark:bg-purple-950/30',
    icon: Layers, 
    label: 'Epic' 
  },
  initiative: { 
    color: 'bg-orange-500', 
    textColor: 'text-orange-600 dark:text-orange-400',
    bgColor: 'bg-orange-50 dark:bg-orange-950/30',
    icon: Rocket, 
    label: 'Initiative' 
  },
  goal: { 
    color: 'bg-yellow-500', 
    textColor: 'text-yellow-600 dark:text-yellow-400',
    bgColor: 'bg-yellow-50 dark:bg-yellow-950/30',
    icon: Target, 
    label: 'Goal' 
  },
  persona: { 
    color: 'bg-green-500', 
    textColor: 'text-green-600 dark:text-green-400',
    bgColor: 'bg-green-50 dark:bg-green-950/30',
    icon: Users, 
    label: 'Persona' 
  },
};

const HarvestRequestsModal = ({
  isOpen,
  onClose,
  items = [],
  productId,
  spaceId,
  spaceName,
  onSuccess,
}) => {
  // Track selected items by index
  const [selectedIndices, setSelectedIndices] = useState(() => 
    new Set(items.map((_, idx) => idx))
  );
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Reset selection when items change
  React.useEffect(() => {
    setSelectedIndices(new Set(items.map((_, idx) => idx)));
  }, [items]);

  const selectedCount = selectedIndices.size;
  const allSelected = selectedCount === items.length;
  const noneSelected = selectedCount === 0;

  const toggleItem = (index) => {
    setSelectedIndices(prev => {
      const next = new Set(prev);
      if (next.has(index)) {
        next.delete(index);
      } else {
        next.add(index);
      }
      return next;
    });
  };

  const selectAll = () => {
    setSelectedIndices(new Set(items.map((_, idx) => idx)));
  };

  const deselectAll = () => {
    setSelectedIndices(new Set());
  };

  const handleSubmit = async () => {
    if (noneSelected || !productId) return;

    setIsSubmitting(true);
    try {
      // Get selected items
      const selectedItems = items
        .filter((_, idx) => selectedIndices.has(idx))
        .map(item => ({
          type: item.type,
          title: item.title,
          description: item.description,
          source_text: item.source_text,
        }));

      // Create requests via API
      const result = await requestsApi.fromCollaboration(productId, {
        space_id: spaceId,
        space_name: spaceName,
        items: selectedItems,
      });

      toast.success(`Created ${result.data.created} request${result.data.created !== 1 ? 's' : ''}`);
      onSuccess?.(result.data.requests);
      onClose();
    } catch (err) {
      console.error('Error creating requests:', err);
      toast.error(err.response?.data?.detail || 'Failed to create requests');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Group items by type for summary
  const typeSummary = useMemo(() => {
    const summary = {};
    items.forEach((item, idx) => {
      if (selectedIndices.has(idx)) {
        summary[item.type] = (summary[item.type] || 0) + 1;
      }
    });
    return summary;
  }, [items, selectedIndices]);

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[600px] max-h-[80vh] flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileInput className="h-5 w-5 text-blue-500" />
            Import Requests from Canvas
          </DialogTitle>
          <DialogDescription>
            Select the items you want to add to your Requests Inbox.
            You can review and edit them after import.
          </DialogDescription>
        </DialogHeader>

        {/* Selection Actions */}
        <div className="flex items-center justify-between py-2 border-b">
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={selectAll}
              disabled={allSelected}
              className="h-8 text-xs"
            >
              <CheckSquare className="h-3 w-3 mr-1" />
              Select All
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={deselectAll}
              disabled={noneSelected}
              className="h-8 text-xs"
            >
              <Square className="h-3 w-3 mr-1" />
              Deselect All
            </Button>
          </div>
          <span className="text-sm text-muted-foreground">
            {selectedCount} of {items.length} selected
          </span>
        </div>

        {/* Items List */}
        <div className="flex-1 overflow-y-auto py-2 space-y-2 min-h-[200px] max-h-[400px]">
          {items.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-muted-foreground">
              <FileInput className="h-8 w-8 mb-2 opacity-50" />
              <p className="text-sm">No items to import</p>
            </div>
          ) : (
            items.map((item, idx) => {
              const config = TYPE_CONFIG[item.type] || TYPE_CONFIG.feature;
              const TypeIcon = config.icon;
              const isSelected = selectedIndices.has(idx);

              return (
                <div
                  key={idx}
                  onClick={() => toggleItem(idx)}
                  className={cn(
                    "p-3 rounded-lg border cursor-pointer transition-all",
                    isSelected 
                      ? "border-blue-500 bg-blue-50/50 dark:bg-blue-950/20" 
                      : "border-border hover:border-muted-foreground/50 opacity-60"
                  )}
                >
                  <div className="flex items-start gap-3">
                    <Checkbox
                      checked={isSelected}
                      onCheckedChange={() => toggleItem(idx)}
                      onClick={(e) => e.stopPropagation()}
                      className="mt-1"
                    />
                    
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <Badge 
                          variant="secondary" 
                          className={cn("text-xs", config.bgColor, config.textColor)}
                        >
                          <TypeIcon className="h-3 w-3 mr-1" />
                          {config.label}
                        </Badge>
                      </div>
                      
                      <h4 className="font-medium text-sm mb-1 truncate">
                        {item.title}
                      </h4>
                      
                      {item.description && (
                        <p className="text-xs text-muted-foreground line-clamp-2 mb-2">
                          {item.description}
                        </p>
                      )}
                      
                      {item.source_text && (
                        <div className="flex items-start gap-1.5 mt-2 pt-2 border-t border-dashed">
                          <Quote className="h-3 w-3 mt-0.5 text-muted-foreground flex-shrink-0" />
                          <span className="text-xs text-muted-foreground italic line-clamp-1">
                            &ldquo;{item.source_text}&rdquo;
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>

        {/* Summary */}
        {selectedCount > 0 && (
          <div className="py-2 border-t">
            <div className="flex flex-wrap gap-1.5">
              {Object.entries(typeSummary).map(([type, count]) => {
                const config = TYPE_CONFIG[type] || TYPE_CONFIG.feature;
                return (
                  <Badge key={type} variant="outline" className="text-xs">
                    <div className={cn("w-2 h-2 rounded-full mr-1.5", config.color)} />
                    {count} {config.label}{count !== 1 ? 's' : ''}
                  </Badge>
                );
              })}
            </div>
          </div>
        )}

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onClose} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button 
            onClick={handleSubmit} 
            disabled={noneSelected || isSubmitting}
          >
            {isSubmitting ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Creating...
              </>
            ) : (
              <>
                <FileInput className="h-4 w-4 mr-2" />
                Create {selectedCount} Request{selectedCount !== 1 ? 's' : ''}
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default HarvestRequestsModal;
